'use client';

import { useState, useEffect } from 'react';
import { ProductDto, PriceDto, useBillingService, CreateProductRequest, UpdateProductRequest } from '../api/billing';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface ManageProductModalProps {
  product: ProductDto | null;
  allPrices: PriceDto[];
  onClose: () => void;
  onSave: () => void;
}

export default function ManageProductModal({ product, allPrices, onClose, onSave }: ManageProductModalProps) {
  const billingService = useBillingService();
  const [productData, setProductData] = useState<any>(null);
  const [pricesData, setPricesData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (product) {
      setProductData({ ...product });
      // Convert cents to dollars for display
      setPricesData(allPrices.filter(p => p.product_id === product.id).map(p => ({ ...p, unit_amount: p.unit_amount / 100 })));
    } else {
      // Initialize for a new product
      setProductData({
        id: `prod_${Date.now()}`,
        name: '',
        description: '',
        stripe_product_id: '',
        is_active: true,
        quotas: '{}',
      });
      setPricesData([]);
    }
  }, [product, allPrices]);

  const handleProductChange = (field: string, value: any) => {
    setProductData({ ...productData, [field]: value });
  };

  const handlePriceChange = (index: number, field: string, value: any) => {
    if (field === 'unit_amount') {
      // Allow only numbers and a single decimal point for price input
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
        return;
      }
    }
    const newPrices = [...pricesData];
    newPrices[index] = { ...newPrices[index], [field]: value };
    setPricesData(newPrices);
  };

  const addPriceRow = () => {
    setPricesData([
      ...pricesData,
      {
        id: `price_${Date.now()}`,
        product_id: productData.id,
        stripe_price_id: '',
        interval: 'month',
        unit_amount: '0',
        currency: 'usd',
        is_active: true,
      },
    ]);
  };

  const removePriceRow = (index: number) => {
    const newPrices = pricesData.filter((_, i) => i !== index);
    setPricesData(newPrices);
  };

  const handleSave = async () => {
    setError(null);
    try {
      // Convert dollar amounts to cents
      const pricesInCents = pricesData.map(p => {
        const amount = parseFloat(p.unit_amount);
        return {
          ...p,
          unit_amount: isNaN(amount) ? 0 : Math.round(amount * 100),
        };
      });

      if (product) { // Update existing product
        const productUpdate: UpdateProductRequest = {
          name: productData.name,
          description: productData.description,
          is_active: productData.is_active,
          quotas: productData.quotas,
          stripe_product_id: productData.stripe_product_id,
        };
        await billingService.updateProduct(product.id, productUpdate);

        // Update prices (this is simplified; a real-world scenario might need to handle new/deleted prices)
        for (const price of pricesInCents) {
          if (allPrices.some(p => p.id === price.id)) { // If price exists, update it
            await billingService.updatePrice(price.id, {
                unit_amount: price.unit_amount,
                is_active: price.is_active,
                stripe_price_id: price.stripe_price_id,
                interval: price.interval,
            });
          } else { // This part of logic for new price is not completed yet, but for now it is fine
            
          }
        }

      } else { // Create new product
        const createRequest: CreateProductRequest = {
          product: productData,
          prices: pricesInCents,
        };
        await billingService.createProduct(createRequest);
      }
      onSave();
    } catch (err) {
      console.error(err);
      setError('Failed to save product. Check the console for details.');
    }
  };

  if (!productData) return null;

  return (
    <div className="fixed inset-0 overflow-y-auto z-50">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
          {product ? `Manage Product: ${product.name}` : 'Create New Product'}
        </h3>
        
        {error && <div className="bg-red-100 text-red-700 p-3 rounded-md mb-4">{error}</div>}

        <div className="space-y-4">
          {/* Product Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-md">
            <h4 className="md:col-span-2 text-lg font-semibold text-gray-800">Product Details</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700">Product Name</label>
              <input type="text" value={productData.name} onChange={(e) => handleProductChange('name', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Stripe Product ID</label>
              <input type="text" value={productData.stripe_product_id} onChange={(e) => handleProductChange('stripe_product_id', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <input type="text" value={productData.description} onChange={(e) => handleProductChange('description', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Quotas (JSON)</label>
              <textarea value={productData.quotas} onChange={(e) => handleProductChange('quotas', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono" rows={4}></textarea>
            </div>
            <div className="flex items-center">
              <input type="checkbox" checked={productData.is_active} onChange={(e) => handleProductChange('is_active', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-900">Product is Active</label>
            </div>
          </div>

          {/* Price Fields */}
          <div className="p-4 border rounded-md">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-semibold text-gray-800">Prices</h4>
              <button onClick={addPriceRow} className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"><PlusIcon className='h-4 w-4 mr-1' /> Add Price</button>
            </div>
            <div className="space-y-3">
              {pricesData.map((price, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-2 border-b">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Stripe Price ID</label>
                    <input type="text" value={price.stripe_price_id} onChange={(e) => handlePriceChange(index, 'stripe_price_id', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Price (in dollars)</label>
                    <input type="text" value={price.unit_amount} onChange={(e) => handlePriceChange(index, 'unit_amount', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., 99.90" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Interval</label>
                    <select value={price.interval} onChange={(e) => handlePriceChange(index, 'interval', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3 pt-5">
                     <div className="flex items-center h-full">
                        <input type="checkbox" checked={price.is_active} onChange={(e) => handlePriceChange(index, 'is_active', e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                        <label className="ml-2 text-sm text-gray-900">Active</label>
                    </div>
                    <button onClick={() => removePriceRow(index)} className="text-red-500 hover:text-red-700"><TrashIcon className="h-5 w-5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button onClick={handleSave} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm">Save Changes</button>
            <button onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
